import { c as _c } from "react/compiler-runtime";
import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, CombinedGraphQLErrors, ServerError } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { useLayoutEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { addDependencyInitializer, exception } from './applicationinsights';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { hasuraUrl, hasuraWssUrl } from './utils';
import { forceLogout, isReauthenticationRequired, isReauthGraphQLError, setReauthHandler, triggerReauth } from './auth';

// Mutable holder for the current Auth0 accessors. The link chain is built once
// (below) and reads from here, so we never rebuild links during render — which
// previously caused requests to race ahead of token acquisition.
const authState: {
  isAuthenticated: boolean;
  getAccessTokenSilently: ((options?: unknown) => Promise<string>) | null;
} = {
  isAuthenticated: false,
  getAccessTokenSilently: null
};

// Log the cause of a re-authentication once, when it is actually triggered.
const reauth = (operationName?: string) => {
  if (triggerReauth()) {
    exception(new Error('Re-authentication required for GraphQL request'), {
      operationName
    });
  }
};

// Acquire an access token, retrying briefly. On first load (especially with
// refresh tokens) getAccessTokenSilently may need a network round-trip and can
// transiently return empty / throw. Returns null only if we genuinely have no
// authenticated session.
const acquireToken = async (operationName?: string): Promise<string | null> => {
  if (!authState.isAuthenticated || !authState.getAccessTokenSilently) {
    return null;
  }
  let token: string | null = null;
  for (let attempt = 0; attempt < 3 && !token; attempt++) {
    try {
      token = await authState.getAccessTokenSilently();
    } catch (e) {
      // A missing/expired refresh token can never be recovered by retrying — the
      // user must sign in again. Skip the remaining attempts and force a logout
      // so they land on the login page instead of a generic error.
      if (isReauthenticationRequired(e)) {
        reauth(operationName);
        return null;
      }
      if (attempt === 2) {
        exception(new Error('Failed to acquire access token for GraphQL request'), {
          msg: e instanceof Error ? e.message : String(e),
          operationName
        });
      }
    }
    if (!token && attempt < 2) {
      await new Promise(resolve => setTimeout(resolve, 150 * (attempt + 1)));
    }
  }
  return token;
};
const graphqlName = (operation: {
  query?: any;
  operationName?: string;
}) => `${(operation.query?.definitions?.[0] as any)?.operation || 'GraphQL'} ${operation.operationName}`;
const httpLink = new HttpLink({
  uri: hasuraUrl,
  fetch
});
const wsLink = new GraphQLWsLink(createClient({
  url: hasuraWssUrl,
  // Configure the JWT into the header
  connectionParams: async () => {
    const token = await acquireToken();
    return {
      headers: {
        authorization: token ? `Bearer ${token}` : null
      },
      next: {
        revalidate: 0
      }
    };
  },
  lazy: true
}));
const authLink = new SetContextLink(async ({
  headers
}, operation) => {
  const token = await acquireToken(operation.operationName);

  // The backend never supports anonymous requests, so refuse to send one. Throwing
  // here surfaces as a network error that RetryLink retries — a request that races
  // ahead of token acquisition self-heals instead of hitting Hasura unauthenticated
  // (which would silently return 0 rows and produce a dead "No organisations" page).
  if (!token) {
    throw new Error(`Aborting unauthenticated GraphQL request: ${operation.operationName}`);
  }
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${token}`,
      graphqlname: graphqlName(operation)
    },
    next: {
      revalidate: 0
    }
  };
});

// Retry transient failures (network blips, or a request that fired before the
// token was ready) before they surface as a hard error to the UI.
const retryLink = new RetryLink({
  delay: {
    initial: 200,
    max: 2000,
    jitter: true
  },
  attempts: {
    max: 4,
    retryIf: error => Boolean(error)
  }
});
const serverErrors = (error: ServerError): ReadonlyArray<unknown> => {
  try {
    return JSON.parse(error.bodyText)?.errors ?? [];
  } catch {
    return [];
  }
};
const errorLink = new ErrorLink(({
  operation,
  error
}) => {
  const operationName = graphqlName(operation);
  const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : [];

  // Re-authenticate when the server rejects our token rather than the token
  // simply being absent (handled in acquireToken). Hasura returns `invalid-jwt`
  // for an expired / invalid JWT — delivered as a GraphQL error, or in the body
  // of a non-2xx ServerError; a bare 401 arrives as a ServerError with `statusCode`.
  const serverError = ServerError.is(error) ? error : null;
  const jwtRejected = [...graphQLErrors, ...(serverError ? serverErrors(serverError) : [])].some(e => isReauthGraphQLError((e as {
    extensions?: {
      code?: unknown;
    };
  })?.extensions?.code));
  const unauthorized = serverError?.statusCode === 401;
  if (jwtRejected || unauthorized) {
    reauth(operationName);
  }
  if (!CombinedGraphQLErrors.is(error)) {
    exception(new Error(`[GraphQL Network error]: ${error}`), {
      ...error,
      operationName
    });
  }
  graphQLErrors.forEach(({
    message,
    extensions
  }) => {
    exception(new Error(`[GraphQL error]: ${extensions?.code}: ${message}`), {
      message,
      ...extensions,
      operationName
    });
  });
});
const splitLink = ApolloLink.split(({
  query
}) => {
  const definition = getMainDefinition(query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
}, wsLink, httpLink);

// Build the link chain once. errorLink sits above retryLink so it only logs the
// final failure (not every retry attempt); retryLink wraps authLink so an aborted
// unauthenticated request is retried once a token becomes available.
const client = new ApolloClient({
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all'
    },
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: false
    }
  },
  link: ApolloLink.from([errorLink, retryLink, authLink, splitLink])
});

// Supress normal graphql dependency calls
addDependencyInitializer(details => {
  if (details.item.target?.startsWith(hasuraUrl())) {
    if (details.item?.properties?.requestHeaders?.graphqlname) {
      details.item.name = details.item.properties.requestHeaders.graphqlname;
      delete details.item.properties.requestHeaders.graphqlname;
    }
  }
});
export const GraphQLProvider = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const {
    getAccessTokenSilently,
    isAuthenticated,
    logout
  } = useAuth0();
  let t1;
  let t2;
  if ($[0] !== getAccessTokenSilently || $[1] !== isAuthenticated || $[2] !== logout) {
    t1 = () => {
      authState.isAuthenticated = isAuthenticated;
      authState.getAccessTokenSilently = getAccessTokenSilently;
      setReauthHandler(() => forceLogout(logout));
    };
    t2 = [isAuthenticated, getAccessTokenSilently, logout];
    $[0] = getAccessTokenSilently;
    $[1] = isAuthenticated;
    $[2] = logout;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  let t3;
  if ($[5] !== children) {
    t3 = <ApolloProvider client={client}>{children}</ApolloProvider>;
    $[5] = children;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};